/*
  Metcos — 3D review carousel.

  Reads the reviews Judge.me already publishes into the shop metafield
  `judgeme.featured_carousel`, which the section prints inside an inert
  <template>. Parsing rather than rendering that markup means:
    - no Judge.me stylesheet is pulled in, and its `.jdgm-carousel{display:none}`
      rule never leaks into the page (a <template>'s contents are inert);
    - the reviews stay in sync automatically, because Judge.me rewrites the
      metafield whenever a review is approved;
    - no API token lives in the theme.

  Dates are deliberately dropped — a two-year-old five-star review reads worse
  than the same review with no date on it.
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function parseReviews(tpl) {
    if (!tpl || !tpl.content) return [];
    var nodes = tpl.content.querySelectorAll('.jdgm-carousel-item');
    var out = [];
    var seen = {};

    Array.prototype.forEach.call(nodes, function (node) {
      var id = node.getAttribute('data-review-id') || '';
      if (id && seen[id]) return;
      seen[id] = true;

      var body = node.querySelector('.jdgm-carousel-item__review-body');
      var text = body ? (body.textContent || '').trim() : '';
      if (!text) return;

      var titleEl = node.querySelector('.jdgm-carousel-item__review-title');
      var nameEl = node.querySelector('.jdgm-carousel-item__reviewer-name');
      var rating = node.querySelectorAll('.jdgm-star.jdgm--on').length;

      out.push({
        rating: rating || 5,
        title: titleEl ? (titleEl.textContent || '').trim() : '',
        body: text,
        name: nameEl ? (nameEl.textContent || '').trim() : ''
      });
    });

    return out;
  }

  function starsMarkup(n) {
    var s = '<span class="mdj-r3d__stars" role="img" aria-label="' + n + ' out of 5 stars">';
    for (var i = 0; i < n; i++) {
      s += '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.45 6.2 20.5l1.1-6.45-4.7-4.6 6.5-.95L12 2.6z"/></svg>';
    }
    return s + '</span>';
  }

  function esc(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function cardMarkup(r, i) {
    var html = '<article class="mdj-r3d__card" data-idx="' + i + '" data-pos="far" aria-roledescription="review">';
    html += starsMarkup(r.rating);
    if (r.title) html += '<p class="mdj-r3d__rev-title">' + esc(r.title) + '</p>';
    html += '<blockquote class="mdj-r3d__quote">' + esc(r.body) + '</blockquote>';
    if (r.name) html += '<p class="mdj-r3d__name">' + esc(r.name) + '</p>';
    // Attribution, not a verification claim: the metafield says which platform
    // the review came from, not whether the reviewer's purchase was confirmed.
    html += '<span class="mdj-r3d__source">via Judge.me</span>';
    html += '</article>';
    return html;
  }

  function init(root) {
    if (root.dataset.mdjReady === '1') return;

    var tpl = root.querySelector('[data-mdj-r3d-data]');
    var reviews = parseReviews(tpl);

    // Nothing to show — hide the whole section rather than leave an empty band.
    if (reviews.length === 0) {
      root.hidden = true;
      return;
    }
    root.dataset.mdjReady = '1';

    var stage = root.querySelector('[data-mdj-r3d-stage]');
    var dotsWrap = root.querySelector('[data-mdj-r3d-dots]');
    var live = root.querySelector('[data-mdj-r3d-live]');
    var prevBtn = root.querySelector('[data-mdj-r3d-prev]');
    var nextBtn = root.querySelector('[data-mdj-r3d-next]');
    var interval = parseInt(root.dataset.autoplay, 10) || 0;

    stage.innerHTML = reviews.map(cardMarkup).join('');
    var cards = Array.prototype.slice.call(stage.querySelectorAll('.mdj-r3d__card'));
    var n = cards.length;
    var current = 0;
    var timer = null;

    if (dotsWrap) {
      dotsWrap.innerHTML = reviews.map(function (_, i) {
        return '<button type="button" class="mdj-r3d__dot" data-idx="' + i + '" aria-label="Review ' + (i + 1) + ' of ' + n + '"></button>';
      }).join('');
    }
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.children) : [];

    // Shortest signed distance around the ring, so wrapping never slides the
    // whole stack sideways.
    function offset(i) {
      var d = i - current;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      return d;
    }

    function render() {
      cards.forEach(function (card, i) {
        var d = offset(i);
        card.dataset.pos = Math.abs(d) <= 2 ? String(d) : 'far';
        card.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
        card.tabIndex = d === 0 ? 0 : -1;
      });
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      if (live) live.textContent = 'Review ' + (current + 1) + ' of ' + n;
    }

    function go(i) {
      current = ((i % n) + n) % n;
      render();
    }

    function next() { go(current + 1); }
    function prev() { go(current - 1); }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!interval || REDUCED.matches || n < 2) return;
      timer = setInterval(next, interval);
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { next(); start(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); start(); });
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () { go(parseInt(dot.dataset.idx, 10)); start(); });
    });
    stage.addEventListener('click', function (e) {
      var card = e.target.closest('.mdj-r3d__card');
      if (card && card.dataset.pos !== '0' && card.dataset.pos !== 'far') {
        go(parseInt(card.dataset.idx, 10));
        start();
      }
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); start(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); start(); }
    });

    // Pointer drag / swipe
    var startX = null;
    stage.addEventListener('pointerdown', function (e) {
      startX = e.clientX;
      stage.classList.add('is-dragging');
      stop();
    });
    stage.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      startX = null;
      stage.classList.remove('is-dragging');
      if (Math.abs(dx) > 40) { dx < 0 ? next() : prev(); }
      start();
    });
    stage.addEventListener('pointercancel', function () {
      startX = null;
      stage.classList.remove('is-dragging');
      start();
    });

    // Don't burn cycles while the section is off screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0.15 }).observe(root);
    } else {
      start();
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    stage.classList.add('is-ready');
    render();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-mdj-r3d]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
